import React, {useState, useCallback} from "react";
import {format} from "date-fns";
import {
    CustomerReviewWrap,
    ReviewWrap,
    SummaryCardsContainer,
    SummaryCard,
    HighlightCardsContainer,
    HighlightCard,
    ReviewsGrid,
    ReviewCard,
} from "components/atoms/Customers/CustomerReviews/CustomerReview.styles";
import ContentHeader from "components/atoms/ContentHeading/ContentHeader";
import {useAuthContext} from "Context/authContext";
import {useSelector} from "react-redux";
import reviewService from "services/reviewService";
import Button from "components/molecules/Button";

// Icons
import forkIcon from "assets/fork.svg";
import scooterIcon from "assets/scooter.svg";
import badIcon from "assets/bad.svg";
import fireIcon from "assets/fire.svg";
import coldIcon from "assets/cold.svg";
import lowIcon from "assets/low.svg";
import {FaStar, FaRegStar, FaStarHalfAlt} from "react-icons/fa";

const POSITIVE_TAGS = [
    "great_taste",
    "good_portions",
    "well_packaged",
    "fresh_ingredients",
    "good_value",
    "delicious",
    "fresh",
    "good_portion",
    "value_for_money",
];
const NEGATIVE_TAGS = [
    "cold_food",
    "small_portions",
    "poor_packaging",
    "missing_items",
    "wrong_order",
    "not_fresh",
    "late",
    "rude",
];

const CustomerReview = () => {
    const {user} = useAuthContext();
    const {business} = useSelector(state => state);
    const restaurantId = user?.businessId?.[0] || user?.businessId || "";
    const storeId = business?.selectedStoreId || business?.defaultStore;

    const [refetch, setRefetch] = useState(0);
    const [searchQuery, setSearchQuery] = useState({
        restaurantId,
        page: 1,
        itemsPerPage: 10,
        searchText: "",
        startDate: "",
        endDate: "",
        status: "",
        items: "",
        storeId,
    });

    // Sync restaurantId and storeId when user loads or header store changes
    React.useEffect(() => {
        setSearchQuery(prev => ({...prev, restaurantId, storeId}));
    }, [restaurantId, storeId]);

    // Fetch paginated reviews list
    const {reviews_loading, reviews_data} = reviewService.GetBusinessReviews(searchQuery, refetch);

    // Fetch review stats + highlights
    const {stats_loading, stats_data, highlights} = reviewService.GetBusinessReviewStats(restaurantId, storeId, refetch);

    const reviews = reviews_data?.items || reviews_data?.reviews || [];
    const totalItems = reviews_data?.totalItems || 0;
    const totalPages = reviews_data?.totalPages || 1;
    const currentPage = reviews_data?.currentPage || 1;

    // Handle reply submission
    const handleReply = useCallback(async (reviewId, replyText) => {
        await reviewService.replyToReview(reviewId, replyText);
        setRefetch(r => r + 1);
    }, []);

    // Pagination
    const handlePageChange = newPage => {
        setSearchQuery(prev => ({...prev, page: newPage}));
    };

    // Search & filter
    const handleSearch = value => {
        setSearchQuery(prev => ({...prev, searchText: value, page: 1}));
    };

    const renderStars = rating => {
        const stars = [];
        const numRating = Number(rating) || 0;
        for (let i = 1; i <= 5; i++) {
            if (i <= numRating) {
                stars.push(<FaStar key={i} />);
            } else if (i === Math.ceil(numRating) && !Number.isInteger(numRating)) {
                stars.push(<FaStarHalfAlt key={i} />);
            } else {
                stars.push(<FaRegStar key={i} />);
            }
        }
        return stars;
    };

    return (
        <CustomerReviewWrap>
            <ContentHeader
                title="Customer Reviews"
                para="Monitor feedback, respond fast, and turn complaints into loyalty."
                placeholder="Search reviews"
                noButton
                setSearchText={setSearchQuery}
                setSearchQuery={setSearchQuery}
                searchQuery={searchQuery}
            />

            {stats_loading ? (
                <div style={{padding: "20px", color: "#666"}}>Loading stats...</div>
            ) : (
                <>
                    <SummaryCardsContainer>
                        <SummaryCard>
                            <div className="icon-wrapper food">
                                <img src={forkIcon} alt="Food Rating" width={20} />
                            </div>
                            <div className="content">
                                <div className="label">Avg Food Rating</div>
                                <div className="value">
                                    {stats_data?.avgFoodRating || 0} <span>/ 5</span>
                                </div>
                            </div>
                        </SummaryCard>
                        <SummaryCard>
                            <div className="icon-wrapper driver">
                                <img src={scooterIcon} alt="Driver Rating" width={24} />
                            </div>
                            <div className="content">
                                <div className="label">Avg Driver Rating</div>
                                <div className="value">
                                    {stats_data?.avgDriverRating || 0} <span>/ 5</span>
                                </div>
                            </div>
                        </SummaryCard>
                        <SummaryCard>
                            <div className="icon-wrapper total">
                                <FaStar color="#ed9321" size={20} />
                            </div>
                            <div className="content">
                                <div className="label">Total Reviews</div>
                                <div className="value">{stats_data?.totalReviews || 0}</div>
                            </div>
                        </SummaryCard>
                        <SummaryCard>
                            <div className="icon-wrapper complaints">
                                <img src={badIcon} alt="Complaints" width={20} />
                            </div>
                            <div className="content">
                                <div className="label">Complaints</div>
                                <div className="value">{stats_data?.complaints || 0}</div>
                            </div>
                        </SummaryCard>
                    </SummaryCardsContainer>

                    <HighlightCardsContainer>
                        <HighlightCard>
                            <div className="icon-wrapper">
                                <img src={fireIcon} alt="Top Rated" width={24} />
                            </div>
                            <div className="content">
                                <div className="label">Top Rated Item</div>
                                <div className="value">{highlights?.topRatedItem?.name || "N/A"}</div>
                                <div className="sub-value">
                                    <FaStar /> {highlights?.topRatedItem?.avgRating || 0} avg rating
                                </div>
                            </div>
                        </HighlightCard>
                        <HighlightCard>
                            <div className="icon-wrapper">
                                <img src={coldIcon} alt="Most Complaints" width={24} />
                            </div>
                            <div className="content">
                                <div className="label">Most Complaints</div>
                                <div className="value">{highlights?.mostComplaintsItem?.name || "N/A"}</div>
                                <div className="sub-value" style={{color: "#999"}}>
                                    {highlights?.mostComplaintsItem?.complaints || 0} mentions
                                </div>
                            </div>
                        </HighlightCard>
                        <HighlightCard>
                            <div className="icon-wrapper">
                                <img src={lowIcon} alt="Lowest Rated" width={24} />
                            </div>
                            <div className="content">
                                <div className="label">Lowest Rated Item</div>
                                <div className="value">{highlights?.lowestRatedItem?.name || "N/A"}</div>
                                <div className="sub-value">
                                    <FaStar /> {highlights?.lowestRatedItem?.avgRating || 0} avg rating
                                </div>
                            </div>
                        </HighlightCard>
                    </HighlightCardsContainer>
                </>
            )}

            <ReviewWrap>
                <div className="title">
                    <h2>
                        Recent Reviews <span>({totalItems})</span>
                    </h2>
                </div>

                {reviews_loading ? (
                    <div style={{color: "#aaa", textAlign: "center", padding: "40px 0"}}>Loading reviews...</div>
                ) : (
                    <>
                        <ReviewsGrid>
                            {reviews.map(review => {
                                const isPositive = review.foodRating > 3;
                                const isNegative =
                                    review.foodRating <= 2 || review.foodTags?.some(t => NEGATIVE_TAGS.includes(t));
                                const sentiment = isNegative ? "negative" : "positive";

                                const itemName = review.order?.items?.[0]?.menuItem?.name || "Order Item";

                                return (
                                    <ReviewCard key={review._id} $sentiment={sentiment}>
                                        <div className="card-inner">
                                            <div className="header">
                                                <div className="item-tag">
                                                    <img
                                                        src={forkIcon}
                                                        alt=""
                                                        width={12}
                                                        style={{
                                                            filter: "brightness(0) saturate(100%) invert(39%) sepia(87%) saturate(2745%) hue-rotate(10deg) brightness(98%) contrast(93%)",
                                                        }}
                                                    />
                                                    {itemName}
                                                </div>
                                                <div className="rating">
                                                    <div className="stars">{renderStars(review.foodRating)}</div>
                                                    {Number(review.foodRating).toFixed(1)}
                                                </div>
                                            </div>

                                            <div className="comment">
                                                "{review.foodComment || review.comment || "No comment provided."}"
                                            </div>

                                            <div className="user-info">
                                                <div className="avatar">
                                                    {review.user?.profile?.first_name?.[0]?.toUpperCase() || "U"}
                                                </div>
                                                <div className="details">
                                                    {review.user?.profile?.first_name} {review.user?.profile?.last_name}{" "}
                                                    <span>• {format(new Date(review.created_at), "MMM d, yyyy")}</span>
                                                </div>
                                            </div>

                                            {review.foodTags && review.foodTags.length > 0 && (
                                                <div className="tags">
                                                    {review.foodTags.map(tag => (
                                                        <span
                                                            key={tag}
                                                            className={`tag ${POSITIVE_TAGS.includes(tag) ? "positive" : NEGATIVE_TAGS.includes(tag) ? "negative" : "neutral"}`}>
                                                            {tag
                                                                .replace(/_/g, " ")
                                                                .replace(/\b\w/g, l => l.toUpperCase())}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            <div className="footer">
                                                <button
                                                    onClick={() => {
                                                        const reply = window.prompt("Enter your reply to this review:");
                                                        if (reply) handleReply(review._id, reply);
                                                    }}>
                                                    Reply
                                                </button>
                                            </div>
                                        </div>
                                    </ReviewCard>
                                );
                            })}
                        </ReviewsGrid>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    gap: 12,
                                    marginTop: 30,
                                    paddingTop: 16,
                                }}>
                                <Button
                                    width="90px"
                                    disabled={currentPage <= 1}
                                    onClick={() => handlePageChange(currentPage - 1)}>
                                    Previous
                                </Button>
                                <span style={{color: "#666", fontSize: 14}}>
                                    Page {currentPage} of {totalPages}
                                </span>
                                <Button
                                    width="90px"
                                    disabled={currentPage >= totalPages}
                                    onClick={() => handlePageChange(currentPage + 1)}>
                                    Next
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </ReviewWrap>
        </CustomerReviewWrap>
    );
};

export default CustomerReview;
